import type { JSX, Ref } from 'react';
import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from 'react';
import type { ArrayIfMultiElseNullable, InfiniteScrollStore } from '../../lib';
import { intl, intlFmt, showError } from '../../lib';
import { useCombobox, useMultipleSelection } from 'downshift';
import _ from 'lodash';
import type { LiveSearchProps } from './LiveSearch';
import {
  BlankState,
  Icon,
  InfiniteScroll,
  List,
  ListItem,
  LoadMask,
  Tool
} from '../basic';
import { HBox, VBox } from '../layout';

export type LiveSearchInlineProps<
  Item extends { tagItem?: () => JSX.Element } | string | Record<string, any>
> = Omit<LiveSearchProps<Item, 'multi'>, 'infiniteScrollStore'> & {
  infiniteScrollStore: InfiniteScrollStore<Item, unknown>;
  renderSelected: (item: Item) => React.ReactNode;
  selectedDescription?: string;
};

function LiveSearchInlineComponent<
  Item extends { tagItem?: () => JSX.Element } | string | Record<string, any>
>({
  itemToKey,
  itemToString,
  onChange,
  loadItems,
  debounceWait = 1000,
  label,
  disabled,
  placeholder,
  renderItem,
  renderSelected,
  initialSearchValue,
  className = '',
  minQueryLength = 1,
  labelOnTop,
  value,
  showSearchIcon,
  infiniteScrollStore,
  selectedDescription,
  ...props
}: Readonly<LiveSearchInlineProps<Item>> & {
  ref?: Ref<HTMLInputElement>;
}): React.JSX.Element {
  type ItemType = Item;
  const [loading, setLoading] = useState(false);
  const [selectableItems, setSelectableItems] = useState<Array<Item>>([]);
  const [inputValue, setInputValue] = useState<string | undefined>(undefined);
  // we remember the most recent query, to prevent querying nonsensical querying
  const [previousSearchQuery, setPreviousSearchQuery] = useState<
    string | undefined
  >(undefined);
  const getSelectedItems = useCallback(
    (
      valueToConvert: ArrayIfMultiElseNullable<'multi', Item> | undefined
    ): ItemType[] => {
      return ((valueToConvert as unknown) as ItemType[] | undefined) ?? [];
    },
    []
  );
  const {
    getDropdownProps,
    addSelectedItem,
    removeSelectedItem,
    selectedItems,
    setSelectedItems
  } = useMultipleSelection<ItemType>({
    getA11yRemovalMessage({ removedSelectedItem }) {
      return intlFmt('common.item.removed', itemToString(removedSelectedItem));
    },
    // this is necessary so that removeSelectedItem knows how to announce the change to the screen reader
    itemToString,
    selectedItems: getSelectedItems(value),
    onSelectedItemsChange: async ({ selectedItems: ms }) => {
      // Convert the selection back into the expected onChange payload
      const arr = (ms ?? []) as ItemType[];
      const multi = (arr as unknown) as ArrayIfMultiElseNullable<'multi', Item>;
      onChange(multi);
    }
  });
  // keep a ref to selectedItems so inputValueChangeCallback doesn't need it as a dependency
  // (otherwise every selection change recreates the debounced fn and cancels the pending call)
  const selectedItemsRef = useRef(selectedItems);
  useEffect(() => {
    selectedItemsRef.current = selectedItems;
  }, [selectedItems]);

  const loadFunction = useCallback(
    (inputValue: string) => {
      loadItems(inputValue)
        .then(items => {
          setSelectableItems(items);
        })
        .catch(e => {
          showError(e);
        })
        .finally(() => setLoading(false));
    },
    [loadItems]
  );

  // loading callbacks
  const inputValueChangeCallback = useCallback(
    (
      inputValue: string | undefined,
      previousSearchQuery: string | undefined
    ) => {
      if (infiniteScrollStore && infiniteScrollStore?.reset) {
        infiniteScrollStore?.reset();
      }
      if (
        (inputValue ?? '').length >= minQueryLength &&
        (previousSearchQuery === undefined ||
          inputValue !== previousSearchQuery)
      ) {
        setPreviousSearchQuery(inputValue);
        setLoading(true);
        if (inputValue) {
          loadFunction(inputValue);
        }
      } else if (inputValue === '') {
        setPreviousSearchQuery(inputValue);
        setSelectableItems([]);
      }
    },
    [infiniteScrollStore, loadFunction, minQueryLength]
  );
  const debouncedInputValueChangeCallback = useMemo(
    () => _.debounce(inputValueChangeCallback, debounceWait),
    [debounceWait, inputValueChangeCallback]
  );

  const reset = () => {
    // after resetting, the previousSearchQuery is treated as being the initial value, meaning undefined
    inputValueChangeCallback('', undefined);
    setInputValue('');
    selectItem((null as unknown) as Item);
  };

  useEffect(() => {
    // when the initialSearchValue changes, we override the value of the input
    // element with the new initialSearchValue, and immediately do the change callback
    // but only if the initialSearchValue is different from what's already entered in the input element
    if (initialSearchValue && initialSearchValue !== inputValue) {
      inputValueChangeCallback(initialSearchValue, undefined);
      setInputValue(initialSearchValue);
    }
    // cheating dependencies since we don't want to depend on the `inputValue`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSearchValue, inputValueChangeCallback]);

  useEffect(() => {
    // if the minQueryLength is explicitly set to 0, we trigger the search as soon as the component is rendered
    // so that the list of results is displayed immediately when opened without the need to type
    if (minQueryLength === 0 && !initialSearchValue) {
      inputValueChangeCallback('', undefined);
    }
  }, [initialSearchValue, inputValueChangeCallback, minQueryLength]);

  // handler for when the input value changes
  // this is a workaround to get around a Downshift bug (https://github.com/downshift-js/downshift/issues/1108)
  // (instead of using useCombobox.stateChangeTypes.InputChange in the switch further down)
  const onInputChange = useCallback(
    (value: string | undefined) => {
      debouncedInputValueChangeCallback(value, previousSearchQuery);
      setInputValue(value);
    },
    [debouncedInputValueChangeCallback, previousSearchQuery]
  );

  const {
    isOpen,
    getLabelProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    selectItem,
    selectedItem
  } = useCombobox({
    inputValue,
    items: selectableItems,
    defaultIsOpen: false,
    initialIsOpen: false,
    scrollIntoView(item) {
      item?.scrollIntoView({ block: 'nearest' });
    }
  });
  const inputProps = getInputProps({
    ...getDropdownProps?.({ preventKeyAction: isOpen }),
    // overriding default onChange to get around a Downshift bug (https://github.com/downshift-js/downshift/issues/1108)
    onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
      onInputChange(event.target.value)
  });
  const screenReaderId = useId();
  const multiSelectScreenReaderHelpTextId = useRef(screenReaderId);
  const ariaLabeledIds = [
    inputProps['aria-labelledby'],
    multiSelectScreenReaderHelpTextId.current
  ]
    .filter(Boolean)
    .join(' ');
  const classNames = ['ehs-live-search-inline', 'ehs-vbox'];
  if (className) {
    classNames.push(className);
  }
  const inputClasses = ['ehs-input-field', 'ehs-select-input-field'];
  if (!inputValue) {
    inputClasses.push('value-empty');
  }
  if (isOpen) {
    inputClasses.push('focused');
  }
  const selectedContainerId = useId();
  const ariaDescribedByIds = [selectedContainerId];
  const placeholderId = useId();
  const inputHintId = useMemo(() => {
    return placeholder ? placeholderId : undefined;
  }, [placeholderId, placeholder]);
  if (inputHintId) {
    ariaDescribedByIds.push(inputHintId);
  }

  const pages = useMemo(() => {
    return infiniteScrollStore.pages.flat();
  }, [infiniteScrollStore.pages]);

  const isSelected = useCallback(
    (item: Item) =>
      selectedItems.some(selected => itemToKey(selected) === itemToKey(item)),
    [selectedItems, itemToKey]
  );

  // downshift removes by reference, so we must pass the stored instance
  // (page items are different instances than the selected ones).
  const findSelected = useCallback(
    (item: Item) =>
      selectedItems.find(selected => itemToKey(selected) === itemToKey(item)),
    [selectedItems, itemToKey]
  );

  return (
    <VBox {...props} className={classNames.join(' ')}>
      <div className={inputClasses.join(' ')}>
        {labelOnTop && (
          <label className="label" {...getLabelProps()}>
            {label}
          </label>
        )}
        <div className="select-input-content">
          {showSearchIcon && (
            <Icon
              aria-label={undefined}
              iconName="search"
              className="search-icon"
            />
          )}
          <div className="select-input-inner">
            <input
              aria-describedby={ariaDescribedByIds.join(' ').trim()}
              aria-labelledby={ariaLabeledIds}
              {...inputProps}
              className="input"
              type="text"
              disabled={disabled}
              // readOnly={readOnly}
              role="combobox"
              aria-expanded={isOpen}
              aria-haspopup="listbox"
              // autoFocus={autoFocus}
            />
            {!labelOnTop && (
              <label
                className="label"
                {...getLabelProps()}
                onClick={
                  // label is inside div with onclick event handling and 'for=#id' attribute creates second click event
                  // we need to stop propagation of click event and thus prevent duplicated event firing
                  (event: MouseEvent) => event.stopPropagation()
                }
              >
                {label}
              </label>
            )}
          </div>
          {inputValue && (
            <Tool
              icon="delete"
              className="clear-tool"
              onClick={() => reset()}
              tooltip={intl('view.button.clear')}
            />
          )}
        </div>
        {placeholder && (
          <p id={inputHintId} className={'ehs-input-hint'}>
            {placeholder}
          </p>
        )}
      </div>
      {selectedItems.length > 0 && (
        <HBox className="selected-container" id={selectedContainerId}>
          <div className="sr-only">{selectedDescription}</div>
          {selectedItems.map((item, index) => (
            <div
              key={label + index}
              className="selected-element"
              aria-selected={true}
            >
              {renderSelected(item)}
            </div>
          ))}
        </HBox>
      )}
      <VBox className={'ehs-inline-dropdown infinite-scrolling-livesearch'}>
        <InfiniteScroll
          endMessage={
            pages.length > 0 ? (
              intl('common.endofData')
            ) : (
              <BlankState
                iconName="user-3"
                description={intl('common.search.noentry')}
              />
            )
          }
          next={() => {
            setLoading(true);
            if (previousSearchQuery) {
              loadFunction(previousSearchQuery);
            }
          }}
          scrollTargetId="dropdown-scroll"
          store={infiniteScrollStore}
        >
          {loading && <LoadMask masked={true} />}
          <List
            className="downshift-menu"
            {...getMenuProps()}
            background={true}
            divider="inset-lef"
          >
            {(pages as Item[]).map((p, idx) => (
              <ListItem
                key={itemToKey(p)}
                className={`menu-item ${
                  highlightedIndex === idx ? 'highlighted' : ''
                } ${isSelected(p) ? 'selected' : ''}`.trim()}
                {...getItemProps({
                  key: itemToKey(p),
                  index: idx,
                  item: p
                })}
                onClick={() => {
                  const existing = findSelected(p);
                  if (existing) {
                    removeSelectedItem(existing);
                  } else {
                    addSelectedItem(p);
                    reset();
                  }
                }}
                aria-selected={isSelected(p)}
              >
                {renderItem ? renderItem(p) : itemToString(p)}
              </ListItem>
            ))}
          </List>
        </InfiniteScroll>
      </VBox>
    </VBox>
  );
}

export const LiveSearchInline = LiveSearchInlineComponent;
